import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { matchPath, Outlet, useMatch, useNavigate, useNavigation } from "react-router-dom";

import {
  getMemberships,
  getPlatform,
  getPlatformUserMemberships,
  getPlatformUserPackages,
  getPromotions,
  getRetailproductsAreRetailProductsVisible,
  getRetailproductsByPlatformIdOrders,
  getSkindropIsVisibleOnMarketplace,
} from "api/methods";
import { useTranslation } from "@/core/i18n";
import TopNav from "molecules/navbars/TopNav";
import { NavRailLink } from "molecules/navigation/nav-rail/NavRail";
import { Tabs } from "molecules/navigation/tabs/Tabs";
import { queryClient } from "pages/Root";
import { notFalsy } from "toolbox/Guards";
import { waitObj } from "toolbox/Promise";
import { hasCancellationRunway } from "utils/membershipStatus";
import { PendingRouteViewport } from "pages/root/PendingRouteViewport";
import { getProductTitle } from "services/HelperService";

import { hasAnyOffersToShow, hasAvailableTreatmentsToSelect } from "./utils";
import {
  WalletDetailSkeleton,
  WalletInitialSkeleton,
  WalletSectionSkeleton,
} from "./WalletPendingContent";
import { prefetchWalletTreatments } from "./Treatments";

const preloadTreatments = () => prefetchWalletTreatments();
const preloadProducts = () =>
  void import("./Products/WalletProductsPage")
    .then(({ prefetchWalletProducts }) => prefetchWalletProducts())
    .catch(() => undefined);
const preloadMemberships = () =>
  void import("./Memberships/WalletMembershipPage")
    .then(({ prefetchWalletMembership }) => prefetchWalletMembership())
    .catch(() => undefined);

const load = async () => {
  const platform = await queryClient.fetchQuery({ ...getPlatform.keyed() });

  const promotions = queryClient.fetchQuery({
    queryKey: getPromotions.key(),
    queryFn: () => getPromotions(),
    staleTime: 1000 * 60 * 2,
  });

  const memberships = queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });
  const treatments = queryClient.fetchQuery({ ...getPlatformUserPackages.keyed() });

  const areRetailProductsVisible = queryClient.fetchQuery({
    ...getRetailproductsAreRetailProductsVisible.keyed(),
    staleTime: 1000 * 10,
  });

  const isSkinDropVisible = queryClient.fetchQuery({
    ...getSkindropIsVisibleOnMarketplace.keyed(),
    staleTime: 1000 * 10,
  });

  const productsOrders = queryClient.fetchQuery({
    ...getRetailproductsByPlatformIdOrders.keyed({ platformId: platform.id }),
  });

  const shopMemberships = queryClient.fetchQuery({
    ...getMemberships.keyed(),
    staleTime: 1000 * 60 * 15,
  });

  const data = await waitObj({
    platform,
    promotions,
    memberships,
    treatments,
    areRetailProductsVisible,
    productsOrders,
    shopMemberships,
    isSkinDropVisible,
  });

  return data;
};

export type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletLayoutQuery = queryOptions({
  queryKey: ["wallet-layout"],
  queryFn: load,
  staleTime: 1000 * 60 * 2,
});
const WalletContent = ({ data }: { data: LoaderData }) => {
  const { t } = useTranslation();
  const {
    platform,
    promotions: initialPromotions,
    memberships,
    treatments,
    areRetailProductsVisible,
    productsOrders,
    shopMemberships,
    isSkinDropVisible,
  } = data;
  const navigate = useNavigate();
  const navigation = useNavigation();
  const isOnWalletRoot = useMatch("/wallet");
  const { data: promotions } = useQuery({
    ...getPromotions.keyed(),
    initialData: initialPromotions,
  });

  // Show Offers tab for available or redeemed offers (within 2 weeks)
  const hasPromotions = hasAnyOffersToShow(promotions);
  const notifyPromotions = hasPromotions;

  const hasMemberships = !!memberships.length;
  const hasActiveMembership = memberships.some(hasCancellationRunway);
  const hasTreatments = !!treatments.length;
  const marketplaceEnabled = !!platform.platformSettings?.marketplaceEnabled;
  const hasRepeatCashEnabled = !!platform.platformSettings?.repeatCashEnabled;
  const notifyMemberships = hasAvailableTreatmentsToSelect(memberships);
  const giftCardsEnabled = !!platform.platformSettings?.giftCardEnabled;
  const hasShopMemberships = !!shopMemberships.length;

  const membershipsEnabled = platform.platformSettings?.membershipsEnabled ?? true;
  const treatmentsAndPackagesEnabled =
    platform.platformSettings?.treatmentsAndPackagesEnabled ?? true;

  // Show Products tab if:
  // - In-store products enabled AND visible, OR
  // - SkinDrop enabled AND visible, OR
  // - User has existing product orders
  // Feature flags for memberships and treatments/packages.
  // When disabled, hide browsing/purchasing options but still show owned items.
  const hasRetailProducts =
    (platform.platformSettings?.myProductsEnabled && !!areRetailProductsVisible) ||
    (platform.platformSettings?.skinDropEnabled && !!isSkinDropVisible) ||
    productsOrders.length > 0;

  const tabs = [
    // Show Treatments tab if:
    // - User has owned treatments (always allow viewing owned items), OR
    // - Marketplace is enabled AND treatmentsAndPackagesEnabled is true (allow browsing/purchasing)
    ((marketplaceEnabled && treatmentsAndPackagesEnabled) || hasTreatments) && {
      to: "/wallet/treatments",
      label: t("walletLayout.treatments"),
      intentPreload: preloadTreatments,
    },
    hasPromotions && {
      notify: notifyPromotions,
      to: "/wallet/offers",
      label: t("walletLayout.offers"),
    },
    hasRetailProducts && {
      to: "/wallet/products",
      label: t("walletLayout.products"),
      intentPreload: preloadProducts,
    },
    // Show Memberships tab if:
    // - User has owned memberships (always allow viewing owned items), OR
    // - Marketplace is enabled AND membershipsEnabled is true AND shop has memberships (allow browsing/purchasing)
    (hasMemberships || (marketplaceEnabled && membershipsEnabled && hasShopMemberships)) && {
      notify: notifyMemberships,
      to: "/wallet/memberships",
      label: t("walletLayout.memberships"),
      intentPreload: preloadMemberships,
    },
    hasRepeatCashEnabled && {
      to: "/wallet/repeatcash",
      label: t("walletLayout.repeatCash"),
    },
    giftCardsEnabled && {
      to: "/wallet/giftcards",
      label: t("walletLayout.giftCards"),
    },
  ].filter(notFalsy);
  const pendingPath = navigation.state === "loading" ? navigation.location?.pathname : undefined;
  const pendingSection = ["/wallet/treatments", "/wallet/products", "/wallet/memberships"].includes(
    pendingPath ?? "",
  );
  const pendingTreatmentId = pendingPath
    ? matchPath("/wallet/treatments/:packageId", pendingPath)?.params.packageId
    : undefined;
  const pendingProductDetail = pendingPath
    ? (matchPath("/wallet/products/order/:orderId", pendingPath) ??
      matchPath("/wallet/products/earned/:packageId", pendingPath))
    : null;
  const pendingMembershipDetail = pendingPath
    ? matchPath({ path: "/wallet/memberships/manage", end: false }, pendingPath)
    : null;
  const pendingDetail = !!pendingTreatmentId || !!pendingProductDetail || !!pendingMembershipDetail;
  const pendingTreatment = treatments.find((item) => item.packageId === pendingTreatmentId);

  useEffect(() => {
    if (!isOnWalletRoot) {
      return;
    }

    if (!tabs.length) {
      navigate("/home");
      return;
    }

    if (hasActiveMembership && tabs.some(({ to }) => to === "/wallet/memberships")) {
      navigate("/wallet/memberships");
      return;
    }

    // Since treatments is usually the first tab, navigate
    // there or to the first available tab whatever it is
    navigate(tabs[0].to);
  }, [isOnWalletRoot, hasActiveMembership, tabs, navigate]);

  return (
    <div className="flex min-h-full flex-col">
      {/* The scrolling tab strip is a phone affordance; from `md` the same
          sections are permanently visible in the rail below. */}
      <TopNav
        footer={
          <div className="desk:hidden">
            <Tabs items={tabs} activeTo={pendingSection ? pendingPath : undefined} />
          </div>
        }
      >
        <h1 className="text-xl">{t("walletLayout.wallet")}</h1>
      </TopNav>
      <div className="flex grow flex-col overflow-hidden bg-one desk:mx-auto desk:grid desk:w-full desk:max-w-screen-xl desk:grow-0 desk:grid-cols-[minmax(0,220px)_minmax(0,1fr)] desk:items-start desk:gap-x-10 desk:overflow-visible desk:px-6 desk:py-8 desklg:gap-x-14 desklg:px-8 desk2xl:max-w-screen-2xl">
        <nav className="hidden desk:sticky desk:top-24 desk:flex desk:flex-col desk:gap-0.5">
          {tabs.map((tab) => (
            <NavRailLink key={tab.to} to={tab.to} notify={tab.notify}>
              {tab.label}
            </NavRailLink>
          ))}
        </nav>
        <div className="mx-auto w-full desk:col-start-2 desk:min-w-0 desk:max-w-none">
          {pendingSection ? (
            <WalletSectionSkeleton />
          ) : pendingDetail ? (
            <PendingRouteViewport>
              <WalletDetailSkeleton
                title={
                  pendingTreatmentId
                    ? t("treatmentPage.header")
                    : pendingProductDetail
                      ? t("walletLayout.products")
                      : t("walletManageMembershipsPage.header")
                }
                name={
                  pendingTreatment
                    ? getProductTitle(pendingTreatment.productName, pendingTreatment.variantName)
                    : undefined
                }
                imageUrl={pendingTreatment?.imageUrl ?? undefined}
              />
            </PendingRouteViewport>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
};

const WalletLayout = () => {
  const { data, error } = useQuery(walletLayoutQuery);
  if (error) throw error;
  return data ? <WalletContent data={data} /> : <WalletInitialSkeleton />;
};

export default WalletLayout;
